/**
 * Pure playlist sequencing math (S13) — kept separate from the timer-driven
 * `usePresentPlaylist` hook so "who's shown, who's on deck, and when" is
 * unit-testable without React or fake timers (elapsed time is an explicit
 * input, not read from the clock).
 */
import type { Dashboard } from '../types/dashboardTypes';

/**
 * Lead time before a cycle during which the NEXT dashboard pre-warms (marked
 * eligible via the S3 `isDashboardEligible` scheduler seam, and mounted
 * off-screen by `DashboardPrewarmMount`) so it never appears empty then
 * bursts once shown.
 */
export const PLAYLIST_PREWARM_LEAD_SECONDS = 10;

/**
 * Ordered, de-duplicated sequence of dashboard ids to cycle through in
 * Present Mode. The presented dashboard's position is PRESERVED if it's
 * already part of the configured list (so a deliberately-ordered playlist
 * like `[B, A, C]` keeps A in the middle); otherwise it's prepended — you
 * always see the dashboard you chose to present first. Ids no longer present
 * in `knownIds` (deleted mid-session; the repository already drops dangling
 * ids on load/import, this is a defensive re-filter against the live set)
 * are dropped.
 */
export function playlistSequence(dashboard: Dashboard, knownIds: ReadonlySet<string>): string[] {
  const configured = (dashboard.playlist?.dashboardIds ?? []).filter((id) => knownIds.has(id));
  const deduped = [...new Set(configured)];
  return deduped.includes(dashboard.id) ? deduped : [dashboard.id, ...deduped];
}

/**
 * Index (into `sequence`) of the entry that should be pre-warming right now,
 * given how long the CURRENT entry (`currentIndex`) has been shown.
 * `null` when there's nothing to pre-warm — a single-dashboard sequence, no
 * cycling configured, or still outside the lead window. When the cycle
 * itself is shorter than the lead time, pre-warm is active for the entire
 * display window (the next dashboard is always kept fresh, not merely at the
 * tail end) rather than never firing.
 */
export function onDeckSequenceIndex(
  sequence: readonly string[],
  currentIndex: number,
  secondsPerDashboard: number,
  elapsedMs: number,
): number | null {
  if (sequence.length < 2 || secondsPerDashboard <= 0) return null;
  const prewarmAtMs = Math.max(0, secondsPerDashboard * 1000 - PLAYLIST_PREWARM_LEAD_SECONDS * 1000);
  if (elapsedMs < prewarmAtMs) return null;
  return (currentIndex + 1) % sequence.length;
}

/**
 * Progress (0..1) through the current dashboard's display window, for Present
 * Mode's auto-advance countdown ring. `null` when nothing is auto-advancing
 * (`cycleMs <= 0` — no cadence configured, or a single-dashboard sequence): the
 * indicator must never imply a countdown that will never fire.
 */
export function playlistCycleProgress(elapsedMs: number, cycleMs: number): number | null {
  if (cycleMs <= 0) return null;
  return Math.min(1, Math.max(0, elapsedMs / cycleMs));
}

/** Whole seconds left before the playlist advances; `null` when not cycling. */
export function playlistSecondsRemaining(elapsedMs: number, cycleMs: number): number | null {
  if (cycleMs <= 0) return null;
  return Math.max(0, Math.ceil((cycleMs - elapsedMs) / 1000));
}
